// The org chart's lenses (CHART-LENSES, docs/design/2026-10-21-chart-lenses.md): the pure maths
// behind the tabs across the top of the chart. KPI Beast coloured every seat card per lens
// (Performance, Responsibilities, Tasks, Financials, Keystones) and combined them into "All"
// with per-role weights; this is the same idea over CompanyOS's own entities, read through
// `GET org-chart/dimensions`:
//   - `responsibilities`: coverage — the share of a seat's responsibilities with measurables
//     (`kpi_metric_ids`); an open seat's responsibilities have no owner: unscored ("—");
//   - `performance`: the seat scorecard's band of the week (its thresholds are the KPI policy's;
//     this module only maps the five bands onto three colours);
//   - `tasks`: the on-time share of the seat's open Work tasks, 100 − overdue %;
//   - `finance`: the seat's budget used this month, in %, amber from `near`, red above `over`;
//   - `execution` (CHART-KB; KPI Beast's Execution = "own goal score"): the scorecard's goal
//     score, the seat's own weighted target attainment of the week, two steps like the KPI
//     policy's goal bands (regular from 85, low under 70);
//   - `keystones` (CHART-KB; KPI Beast's Keystones): the share of the seat's quarterly
//     priorities (`goal.kind = priority`) that are on track, in %;
//   - `completeness` (CHART-KB, the founder's seat setup %): not a score of the work but of the
//     setup — `seat-completeness.ts`; it is a lens with a number and never a part of Overall;
//   - `overall`: the weighted mean of the scored lenses, each as a 0–100 goodness, over the
//     lenses that have data (a lens without data lowers coverage, never the score; weight 0
//     leaves it out), and only once enough of it is scored (`OVERALL_MIN_SCORED` lenses or half
//     the weight): a seat with one lens of six is unscored, not 100.
// Unscored is `score: null`, band "none": the card reads "—" and a roll-up skips the seat.
// The API computes `overall` and the client the rest, both from here, so the fake twin, the
// seat card and the side panel agree to the digit. Thresholds and weights are the company's
// (`settings.chart.lenses`) over the defaults below.
import type { Band } from "@companyos/domain-kpi";

export type Lens =
  | "structure"
  | "responsibilities"
  | "performance"
  | "execution"
  | "tasks"
  | "finance"
  | "keystones"
  | "completeness"
  | "overall";
/** The lenses in tab order (KPI Beast's: Performance, Execution, Responsibilities, Tasks, Financials, Keystones). */
export const LENSES: readonly Lens[] = [
  "structure",
  "performance",
  "execution",
  "responsibilities",
  "tasks",
  "finance",
  "keystones",
  "overall",
  "completeness",
];
/** The lenses with a score: the parts of Overall. */
export type ScoredLens =
  | "responsibilities"
  | "performance"
  | "execution"
  | "tasks"
  | "finance"
  | "keystones";
export const SCORED_LENSES: readonly ScoredLens[] = [
  "performance",
  "execution",
  "responsibilities",
  "tasks",
  "finance",
  "keystones",
];

export type LensBand = "green" | "amber" | "red" | "none";
export type TwoSteps = { green: number; amber: number };
export type FinanceSteps = { near: number; over: number };
export type LensThresholds = {
  responsibilities: TwoSteps;
  execution: TwoSteps;
  tasks: TwoSteps;
  finance: FinanceSteps;
  keystones: TwoSteps;
  overall: TwoSteps;
};
export type LensWeights = Record<ScoredLens, number>;

/**
 * The steps a company starts with. Responsibilities: four in five measurable is green. Tasks:
 * green with at most 15 % overdue (KPI Beast allowed none; most teams carry a little), red once
 * half the open work is overdue.
 * Finance: the budget-vs-actual states (near from 80 %, over above 100 %). Execution: the KPI
 * policy's goal bands (regular from 85, low under 70). Keystones: four in five on track is
 * green, half amber. Overall: 80 / 60.
 */
export const DEFAULT_LENS_THRESHOLDS: LensThresholds = {
  responsibilities: { green: 80, amber: 50 },
  execution: { green: 85, amber: 70 },
  tasks: { green: 85, amber: 50 },
  finance: { near: 80, over: 100 },
  keystones: { green: 80, amber: 50 },
  overall: { green: 80, amber: 60 },
};

/** Performance counts double: the scorecard is the seat's own measure of the week. */
export const DEFAULT_LENS_WEIGHTS: LensWeights = {
  responsibilities: 1,
  performance: 2,
  execution: 1,
  tasks: 1,
  finance: 1,
  keystones: 1,
};

type Loose<T> = { [K in keyof T]?: T[K] | undefined };
export type PartialLensThresholds = {
  responsibilities?: Loose<TwoSteps> | undefined;
  execution?: Loose<TwoSteps> | undefined;
  tasks?: Loose<TwoSteps> | undefined;
  finance?: Loose<FinanceSteps> | undefined;
  keystones?: Loose<TwoSteps> | undefined;
  overall?: Loose<TwoSteps> | undefined;
};

const over = <T extends object>(base: T, set: Loose<T> | undefined): T => {
  const out = { ...base };
  for (const [k, v] of Object.entries(set ?? {}))
    if (v !== undefined) (out as Record<string, unknown>)[k] = v;
  return out;
};

/** The company's steps over the defaults (a missing key, or a missing step, takes the default). */
export function lensThresholdsOf(set: PartialLensThresholds | null | undefined): LensThresholds {
  const d = DEFAULT_LENS_THRESHOLDS;
  return {
    responsibilities: over(d.responsibilities, set?.responsibilities),
    execution: over(d.execution, set?.execution),
    tasks: over(d.tasks, set?.tasks),
    finance: over(d.finance, set?.finance),
    keystones: over(d.keystones, set?.keystones),
    overall: over(d.overall, set?.overall),
  };
}

/** The company's weights over the defaults. */
export function lensWeightsOf(set: Loose<LensWeights> | null | undefined): LensWeights {
  return over(DEFAULT_LENS_WEIGHTS, set ?? undefined);
}

/** A lens's answer for one seat: the number the card shows, its band, and why when there is none. */
export type LensScore = {
  score: number | null;
  band: LensBand;
  /** What the card says instead of a number ("No open tasks", "No budget"). */
  note?: string;
};

const NONE = (note: string): LensScore => ({ score: null, band: "none", note });
const round = (n: number) => Math.round(n);

/** Two steps down: green at or above `green`, amber at or above `amber`, red below; null is none. */
export function stepBand(score: number | null, t: TwoSteps): LensBand {
  if (score === null || !Number.isFinite(score)) return "none";
  if (score >= t.green) return "green";
  if (score >= t.amber) return "amber";
  return "red";
}

/** The scorecard's five bands on three colours: great and regular are green, average amber, low red. */
export const LENS_BAND_OF_SCORECARD: Record<Band, LensBand> = {
  great: "green",
  regular: "green",
  average: "amber",
  low: "red",
  insufficient_data: "none",
};

/**
 * Responsibilities: coverage in %, the share with measurables. No responsibilities is nothing
 * to score; an open seat (no holder) owns none of them: unscored, not a red 0 (nobody failed).
 */
export function responsibilitiesScore(
  d: { count: number; measurable: number } | null | undefined,
  held: boolean,
  t: TwoSteps,
): LensScore {
  if (!d || d.count === 0) return NONE("None yet");
  if (!held) return NONE("No owner");
  const score = round((Math.min(d.measurable, d.count) / d.count) * 100);
  return { score, band: stepBand(score, t) };
}

/** Performance: the scorecard's KPI score and band; "awaiting data" below the completeness cutoff. */
export function performanceScore(
  kpi: { band: Band; kpi_score: number | null } | null | undefined,
): LensScore {
  if (kpi === undefined || kpi === null) return NONE("Not scored");
  const band = LENS_BAND_OF_SCORECARD[kpi.band];
  if (band === "none") return NONE("Awaiting data");
  return { score: kpi.kpi_score === null ? null : round(kpi.kpi_score), band };
}

/**
 * Execution (CHART-KB): the scorecard's goal score — the seat's weighted target attainment of
 * the week (KPI Beast's "own goal score") — on two steps; a seat without metrics or without
 * enough data this week is unscored.
 */
export function executionScore(
  kpi: { band: Band; goal_score: number | null } | null | undefined,
  t: TwoSteps,
): LensScore {
  if (kpi === undefined || kpi === null) return NONE("Not scored");
  if (kpi.band === "insufficient_data" || kpi.goal_score === null) return NONE("Awaiting data");
  const score = round(kpi.goal_score);
  return { score, band: stepBand(score, t) };
}

/**
 * Keystones (CHART-KB): the share of the quarter's priorities on track, in %. No priorities
 * this quarter is nothing to score ("None this quarter"); at-risk ones count half, so three
 * priorities with one on, one at risk and one off read 50.
 */
export function keystonesScore(
  k: { count: number; on: number; at_risk: number } | null | undefined,
  t: TwoSteps,
): LensScore {
  if (!k || k.count === 0) return NONE("None this quarter");
  const score = round(((k.on + k.at_risk / 2) / k.count) * 100);
  return { score, band: stepBand(score, t) };
}

/** Tasks: the on-time share of the open work, 100 − overdue %; no open tasks is nothing to score. */
export function tasksScore(
  load: { open: number; overdue: number } | null | undefined,
  t: TwoSteps,
): LensScore {
  if (!load || load.open === 0) return NONE("No open tasks");
  const score = round(100 * (1 - Math.min(load.overdue, load.open) / load.open));
  return { score, band: stepBand(score, t) };
}

/** Finance: the budget used in % (actual / budget); no budget or no utilisation is nothing to score. */
export function financeScore(
  cost: { budget_minor: number | null; utilization: string | null } | null | undefined,
  t: FinanceSteps,
): LensScore {
  if (cost === undefined || cost === null) return NONE("No cost");
  if (cost.budget_minor === null || cost.utilization === null) return NONE("No budget");
  const used = Number(cost.utilization);
  if (!Number.isFinite(used)) return NONE("No budget");
  const score = round(used * 100);
  const band: LensBand = score > t.over ? "red" : score >= t.near ? "amber" : "green";
  return { score, band };
}

/**
 * Budget used as a 0–100 goodness for Overall: 100 up to `near`, down to 50 at `over`, down to
 * 0 another (over − near) beyond it (so 80 / 100: 90 % used is 75, 110 % is 25, 120 % is 0).
 */
export function financeGoodness(usedPct: number, t: FinanceSteps): number {
  const span = Math.max(1, t.over - t.near);
  if (usedPct <= t.near) return 100;
  if (usedPct <= t.over) return round(100 - ((usedPct - t.near) / span) * 50);
  return Math.max(0, round(50 - ((usedPct - t.over) / span) * 50));
}

/** A scored lens as a 0–100 goodness (finance through its curve, the others capped at 100). */
export function lensGoodness(lens: ScoredLens, s: LensScore, t: LensThresholds): number | null {
  if (s.score === null || s.band === "none") return null;
  if (lens === "finance") return financeGoodness(s.score, t.finance);
  return Math.max(0, Math.min(100, s.score));
}

export type OverallScore = { score: number | null; band: LensBand; scored: number; total: number };

/** Overall needs this many scored lenses… */
export const OVERALL_MIN_SCORED = 2;
/** …or this share of the served weight scored (one lens carrying half the weight is enough). */
export const OVERALL_MIN_WEIGHT_SHARE = 0.5;

/**
 * Overall: the weighted mean of the scored lenses' goodness over the ones with data. `total`
 * counts the lenses with a weight above 0 that the seat could carry (a part passed as undefined
 * is not served at all: no KPI add-on); `scored` the ones that had a number. Too little scored
 * (fewer than `OVERALL_MIN_SCORED` lenses and under `OVERALL_MIN_WEIGHT_SHARE` of the weight)
 * is null and "none", like no data at all: one green lens of four is not a 100.
 */
export function overallScore(
  parts: Partial<Record<ScoredLens, LensScore>>,
  weights: LensWeights,
  t: LensThresholds,
): OverallScore {
  let sum = 0;
  let wsum = 0;
  let wtotal = 0;
  let scored = 0;
  let total = 0;
  for (const lens of SCORED_LENSES) {
    const w = weights[lens];
    const part = parts[lens];
    if (!(w > 0) || part === undefined) continue;
    total += 1;
    wtotal += w;
    const g = lensGoodness(lens, part, t);
    if (g === null) continue;
    scored += 1;
    sum += w * g;
    wsum += w;
  }
  const enough =
    wsum > 0 && (scored >= OVERALL_MIN_SCORED || wsum / wtotal >= OVERALL_MIN_WEIGHT_SHARE);
  const score = enough ? round(sum / wsum) : null;
  return { score, band: stepBand(score, t.overall), scored, total };
}

/**
 * A branch's roll-up for a lens: the plain mean of the scores present under a manager (its own
 * seat included), null when none has one; an unscored seat (null) is skipped, never a 0.
 * Finance rolls up as the mean budget used.
 */
export function branchRollup(scores: readonly (number | null)[]): number | null {
  const present = scores.filter((s): s is number => s !== null && Number.isFinite(s));
  if (!present.length) return null;
  return round(present.reduce((a, b) => a + b, 0) / present.length);
}

/** The band of a lens's number, for a roll-up drawn with the lens's own steps. */
export function bandOf(
  lens: ScoredLens | "overall",
  score: number | null,
  t: LensThresholds,
): LensBand {
  if (score === null) return "none";
  switch (lens) {
    case "finance":
      return score > t.finance.over ? "red" : score >= t.finance.near ? "amber" : "green";
    case "performance":
      // The scorecard's steps are the KPI policy's; a roll-up of KPI scores reads with Overall's.
      return stepBand(score, t.overall);
    default:
      return stepBand(score, t[lens]);
  }
}
